// Colour maths (no dependencies): OKLCH <-> sRGB with chroma-reduction gamut fitting (the UX §6.1 method,
// same as tests/data/presentation.test.ts), CIELAB ΔE76 and WCAG contrast for tests/art.

export type Rgb = readonly [number, number, number]; // 0–255 integers
export type Oklch = readonly [number, number, number]; // L 0–1, C, h degrees

const DEG = Math.PI / 180;

function oklabToLinear(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = (rgb: readonly number[]): boolean => rgb.every((v) => v >= -1e-6 && v <= 1 + 1e-6);

function encode(c: number): number {
  const x = Math.min(1, Math.max(0, c));
  const g = x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
  return Math.round(g * 255);
}

function decode(c8: number): number {
  const c = c8 / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** OKLCH → sRGB bytes; out-of-gamut colours keep L and h and lose chroma (binary search). */
export function oklchToRgb([L0, C0, h]: Oklch): Rgb {
  const L = Math.min(1, Math.max(0, L0));
  const C = Math.max(0, C0);
  const lin = (c: number) => oklabToLinear(L, c * Math.cos(h * DEG), c * Math.sin(h * DEG));
  let rgb = lin(C);
  if (!inGamut(rgb)) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(lin(mid))) lo = mid;
      else hi = mid;
    }
    rgb = lin(lo);
  }
  return [encode(rgb[0]), encode(rgb[1]), encode(rgb[2])];
}

export function rgbToOklch([r8, g8, b8]: Rgb): [number, number, number] {
  const r = decode(r8);
  const g = decode(g8);
  const b = decode(b8);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.hypot(A, B);
  let h = Math.atan2(B, A) / DEG;
  if (h < 0) h += 360;
  return [L, C, h];
}

export function hex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function parseHex(s: string): Rgb {
  const m = /^#([0-9a-f]{6})$/i.exec(s);
  if (!m?.[1]) throw new Error(`bad hex colour ${s}`);
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** sRGB mix: a·(1−t) + b·t, rounded to bytes. */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

// ---- CIELAB (D65) and ΔE76 -----------------------------------------------------------------------------

export function rgbToLab([r8, g8, b8]: Rgb): [number, number, number] {
  const r = decode(r8);
  const g = decode(g8);
  const b = decode(b8);
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
  const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 / 116) * t + 16 / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

export function deltaE(a: readonly number[], b: readonly number[]): number {
  return Math.hypot(
    (a[0] ?? 0) - (b[0] ?? 0),
    (a[1] ?? 0) - (b[1] ?? 0),
    (a[2] ?? 0) - (b[2] ?? 0),
  );
}

// ---- WCAG relative luminance / contrast ----------------------------------------------------------------

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * decode(r) + 0.7152 * decode(g) + 0.0722 * decode(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
